import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import type { RenderPipeline } from './RenderPipeline';

export interface BloomOptions {
  strength: number;
  radius: number;
  threshold: number;
}

const cinematicShader = {
  uniforms: {
    tDiffuse: { value: null },
    uVignette: { value: 0.55 },
    uAberration: { value: 0.0035 },
    uExposureBoost: { value: 1.04 }
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uVignette;
    uniform float uAberration;
    uniform float uExposureBoost;
    varying vec2 vUv;

    void main() {
      vec2 center = vec2(0.5);
      vec2 toCenter = vUv - center;
      float dist = length(toCenter);

      // Chromatic aberration grows quadratically toward the edges.
      float aberr = uAberration * dist * dist;
      vec2 dir = toCenter / max(dist, 0.0001);
      vec3 col;
      col.r = texture2D(tDiffuse, vUv + dir * aberr).r;
      col.g = texture2D(tDiffuse, vUv).g;
      col.b = texture2D(tDiffuse, vUv - dir * aberr).b;

      // Subtle exposure breathing.
      col *= uExposureBoost;

      // Vignette: darken corners with smooth falloff.
      float v = 1.0 - smoothstep(0.42, 0.98, dist) * uVignette;
      col *= v;

      gl_FragColor = vec4(col, 1.0);
    }
  `
};

// Bloom is a low-frequency effect — running it at half the screen resolution
// is ~4x cheaper on the GPU and visually indistinguishable from full-res at
// the radii we use. The cinematic shader (chromatic aberration, vignette)
// still runs full-res so fine detail isn't blurred.
const BLOOM_RESOLUTION_SCALE = 0.5;

export class BloomPipeline implements RenderPipeline {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly composer: EffectComposer;
  private readonly renderPass: RenderPass;
  private readonly bloomPass: UnrealBloomPass;
  private activeScene: THREE.Scene | null = null;
  private activeCamera: THREE.Camera | null = null;
  private bypass = false;

  constructor(
    renderer: THREE.WebGLRenderer,
    size: { width: number; height: number },
    options: BloomOptions
  ) {
    this.renderer = renderer;
    this.composer = new EffectComposer(renderer);
    this.composer.setSize(size.width, size.height);

    const placeholderScene = new THREE.Scene();
    const placeholderCamera = new THREE.PerspectiveCamera();

    this.renderPass = new RenderPass(placeholderScene, placeholderCamera);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(
        size.width * BLOOM_RESOLUTION_SCALE,
        size.height * BLOOM_RESOLUTION_SCALE
      ),
      options.strength,
      options.radius,
      options.threshold
    );

    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(new ShaderPass(cinematicShader));
    this.composer.addPass(new OutputPass());
  }

  /** Skip the post-processing chain and render directly. Useful as a fast
   *  path when the player opts out of effects or hardware can't keep up —
   *  flipping at runtime is cheaper than rebuilding the pipeline. */
  setBypass(bypass: boolean): void {
    this.bypass = bypass;
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    if (this.bypass) {
      this.renderer.render(scene, camera);
      return;
    }
    if (this.activeScene !== scene) {
      this.renderPass.scene = scene;
      this.activeScene = scene;
    }
    if (this.activeCamera !== camera) {
      this.renderPass.camera = camera;
      this.activeCamera = camera;
    }
    this.composer.render();
  }

  resize(width: number, height: number): void {
    this.composer.setSize(width, height);
    this.bloomPass.setSize(
      width * BLOOM_RESOLUTION_SCALE,
      height * BLOOM_RESOLUTION_SCALE
    );
  }

  dispose(): void {
    this.composer.dispose();
  }
}
